import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SpinnerOverlay } from "../../../Components/LoadingAnimations/SpinnerOverlay";
import FormBlock from "../../../Components/Inputs/FormBlock";
import instance from "../../../Interceptor/api_instance";
import { TrashIconCont } from "../../../Media/Icons";
import { ActionResponse } from "../../../Components/Modals/ActionResponses";
import MechanicSearch from "../../../Components/Inputs/MechanicSearch";

export const NewMaintenanceTask = ({
  close,
  setData,
  maintenance_report_id,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    description: "",
    maintenance_report: maintenance_report_id,
  });
  const [responseModal, setResponseModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateFormData = (e) => {
    const { name, value } = e.target;

    // If you know it's an ID, cast to number:
    setFormData({
      ...formData,
      [name]: name === "assigned_mechanic" ? Number(value) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const bodyData = {
        ...formData,
        assigned_mechanic: formData.assigned_mechanic.value,
      };
      const response = await instance.post(
        "safety/maintenance-tasks/",
        bodyData
      );
      if (response.status === 201) {
        setResponseModal({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });

        // Update the parent component's state with the new task
        const responseData = response.data;
        setData((prev) => ({
          ...prev,
          status: responseData.report_patch.status,
          tasks: [...prev.tasks, responseData.task],
        }));
      }
    } catch (error) {
      setResponseModal({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-mask page-mask-animation">
      {isLoading && <SpinnerOverlay />}
      {responseModal && (
        <ActionResponse
          status={responseModal.status}
          message={responseModal.message}
          close={() => {
            if (responseModal.status === t("forms.type.success")) {
              setResponseModal(null);
              close();
            } else {
              setResponseModal(null);
            }
          }}
        />
      )}
      <div className="new-modal">
        <h3 className="global-h3">
          {t("forms.type.maintenance-report.newTask")}
        </h3>
        <form onSubmit={handleSubmit}>
          <FormBlock
            label={"Description"}
            name={"description"}
            value={formData.description}
            update={updateFormData}
            isRequired={true}
          />
          <div className="form-block">
            <label className="input-label required">
              {t("forms.type.maintenance-report.labels.taskType")}
            </label>
            <div className="input-cont">
              <fieldset className="fieldsets">
                <div className="radio-options">
                  <input
                    type="radio"
                    name="task_type"
                    data-testid="taskType1"
                    className="radio-input"
                    value={"corrective"}
                    checked={formData.task_type === "corrective"}
                    onChange={updateFormData}
                    required
                  />
                  <label className="radio-label">
                    {t("forms.type.maintenance-report.labels.corrective")}
                  </label>
                </div>
                <div className="fieldsets">
                  <div className="radio-options">
                    <input
                      type="radio"
                      name="task_type"
                      data-testid="taskType2"
                      className="radio-input"
                      value={"preventive"}
                      checked={formData.task_type === "preventive"}
                      onChange={updateFormData}
                      required
                    />
                    <label className="radio-label">
                      {t("forms.type.maintenance-report.labels.preventive")}
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div className="form-block">
            <label className="input-label">
              {t("forms.type.visitor.purpose.mechanic")}
            </label>
            <div className="input-cont">
              <MechanicSearch
                selectedOption={formData.assigned_mechanic}
                onSelect={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_mechanic: option,
                  }))
                }
              />
            </div>
          </div>
          <div className="full-cont">
            <button className="change-pwd-btn" type="submit">
              {t("buttons.submit")}
            </button>
            <button
              className="all-btn cancel-btn"
              type="button"
              onClick={close}
            >
              {t("buttons.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const EditMaintenanceTask = ({ data, close, setData }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    description: data.description,
    status: data.status,
    id: data.id,
    assigned_mechanic: {
      value: data.assigned_mechanic_detail?.id,
      label: data.assigned_mechanic_detail?.full_name,
    },
    task_type: data.task_type,
    notes: data.notes,
  });
  const [responseModal, setResponseModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateFormData = (e) => {
    const { name, value } = e.target;

    // If you know it's an ID, cast to number:
    setFormData({
      ...formData,
      [name]: name === "assigned_mechanic" ? Number(value) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const bodyData = {
        ...formData,
        assigned_mechanic: formData.assigned_mechanic.value,
      };
      const response = await instance.patch(
        `safety/maintenance-tasks/${formData.id}/`,
        bodyData
      );
      if (response.status === 200) {
        setResponseModal({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });
        // Update the parent component's state with the updated task
        const responseData = response.data;
        setData((prev) => ({
          ...prev,
          status: responseData.report_patch.status,
          tasks: prev.tasks.map((task) =>
            task.id === formData.id ? responseData.task : task
          ),
        }));
      }
    } catch (error) {
      setResponseModal({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const response = await instance.delete(
        `safety/maintenance-tasks/${data.id}/`
      );
      if (response.status === 200) {
        setResponseModal({
          status: t("forms.type.success"),
          message: t("forms.responses.success"),
        });

        const responseData = response.data;
        setData((prev) => ({
          ...prev,
          status: responseData.report_patch.status,
          tasks: prev.tasks.filter((task) => task.id !== formData.id),
        }));
      }
    } catch (error) {
      setResponseModal({
        status: "Error",
        message: error,
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="page-mask page-mask-animation">
      {isLoading && <SpinnerOverlay />}
      {responseModal && (
        <ActionResponse
          status={responseModal.status}
          message={responseModal.message}
          close={() => {
            if (responseModal.status === t("forms.type.success")) {
              setResponseModal(null);
              close();
            } else {
              setResponseModal(null);
            }
          }}
        />
      )}
      <div className="new-modal">
        <div className="content-between">
          <h3 className="global-h3">
            {t("forms.type.maintenance-report.edit-task")}
          </h3>
          <TrashIconCont onClick={() => handleDelete()} />
        </div>
        <form onSubmit={handleSubmit}>
          <FormBlock
            label={"Description"}
            name={"description"}
            value={formData.description}
            update={updateFormData}
            isRequired={true}
          />
          <div className="form-block">
            <label className="input-label required">
              {t("forms.type.maintenance-report.labels.taskType")}
            </label>
            <div className="input-cont">
              <fieldset className="fieldsets">
                <div className="radio-options">
                  <input
                    type="radio"
                    name="task_type"
                    data-testid="taskType1"
                    className="radio-input"
                    value={"corrective"}
                    checked={formData.task_type === "corrective"}
                    onChange={updateFormData}
                    required
                  />
                  <label className="radio-label">
                    {t("forms.type.maintenance-report.labels.corrective")}
                  </label>
                </div>
                <div className="fieldsets">
                  <div className="radio-options">
                    <input
                      type="radio"
                      name="task_type"
                      data-testid="taskType2"
                      className="radio-input"
                      value={"preventive"}
                      checked={formData.task_type === "preventive"}
                      onChange={updateFormData}
                      required
                    />
                    <label className="radio-label">
                      {t("forms.type.maintenance-report.labels.preventive")}
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div className="form-block">
            <label className="input-label">
              {t("forms.type.visitor.purpose.mechanic")}
            </label>
            <div className="input-cont">
              <MechanicSearch
                selectedOption={formData.assigned_mechanic}
                onSelect={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_mechanic: option,
                  }))
                }
              />
            </div>
          </div>
          <div className="form-block">
            <label className="input-label">
              {t("yard-tables.table-columns.status")}
            </label>
            <div className="input-cont">
              <select
                onChange={updateFormData}
                value={formData.status}
                name="status"
              >
                <option value="pending">
                  {t(`forms.type.maintenance-report.status-options.pending`)}
                </option>
                <option value="waiting-parts">
                  {t("forms.type.maintenance-report.status-options.waiting-parts")}
                </option>
                <option value="in-progress">
                  {t(
                    "forms.type.maintenance-report.status-options.in-progress"
                  )}
                </option>
                <option value="completed">
                  {t("forms.type.maintenance-report.status-options.completed")}
                </option>
              </select>
            </div>
          </div>
          <div className="form-block">
            <label
              className={`input-label ${
                formData.status === "completed" ? "required" : ""
              }`}
            >
              {t("forms.type.vehicle.labels.notes")}
            </label>
            <div className="input-cont">
              <textarea
                className="text-input with-border"
                onChange={updateFormData}
                name="notes"
                value={formData.notes}
                required={formData.status === "completed"}
              ></textarea>
            </div>
          </div>
          <div className="full-cont">
            <button className="change-pwd-btn" type="submit">
              {t("buttons.submit")}
            </button>
            <button
              className="all-btn cancel-btn"
              type="button"
              onClick={close}
            >
              {t("buttons.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
